/**
 * Light / dark / follow-the-system.
 *
 * mail.css has carried a full `[data-theme="light"]` palette since the design
 * was ported, with nothing ever setting the attribute. This is that switch.
 *
 * The preference lives in localStorage rather than sessionStorage: unlike the
 * access token, which should die with the tab, a theme choice that resets every
 * time you open the mailbox is just an annoyance.
 */

export type ThemeChoice = "system" | "light" | "dark";

const KEY = "syndeo.mail.theme";

/**
 * Light unless asked otherwise.
 *
 * Mail is mostly other people's HTML, and almost all of it is authored for a
 * white background — a dark shell around a white message body is the worst of
 * both. "System" remains available for anyone who wants it to follow the OS,
 * but it is not the default: the default should match what the content is.
 */
export const DEFAULT_THEME: ThemeChoice = "light";

export function storedTheme(): ThemeChoice {
  try {
    const value = localStorage.getItem(KEY);
    return value === "light" || value === "dark" || value === "system" ? value : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/** Resolves "system" against the OS setting and stamps the root element. */
export function applyTheme(choice: ThemeChoice): void {
  const dark =
    choice === "dark" ||
    (choice === "system" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  // The dark palette is the :root default, so only light needs the attribute.
  if (dark) document.documentElement.removeAttribute("data-theme");
  else document.documentElement.setAttribute("data-theme", "light");
}

export function storeTheme(choice: ThemeChoice): void {
  try {
    // Every choice is stored, including the default — otherwise "system" would
    // be indistinguishable from "never chose", and changing the default later
    // would silently move people who had deliberately picked the old one.
    localStorage.setItem(KEY, choice);
  } catch {
    /* private browsing — the choice just won't survive a reload */
  }
  applyTheme(choice);
}

/**
 * Keeps "system" honest: if the OS flips to dark at sunset the app follows,
 * but an explicit choice is never overridden.
 */
export function watchSystemTheme(getChoice: () => ThemeChoice): () => void {
  const media = window.matchMedia?.("(prefers-color-scheme: dark)");
  if (!media) return () => {};
  const onChange = () => {
    if (getChoice() === "system") applyTheme("system");
  };
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
